import React, {
  useState, useEffect, forwardRef, useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import { PlusOutlined } from '@ant-design/icons';
import {
  message, notification, Spin, Upload,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import './UploadFiles.scss';
import {
  get, isSuperAgentError,
  RequestWrapper, ResponseWrapper,
} from '../../../../dataSource/requests';
import { downloadFile } from '../../../../utils/helpers';
import { updateFiles, UploadFilesReports } from './requests';
import { getUserType } from '../../../../utils/users';
import imgPdf from '../../../../assets/images/icon-pdf.svg';
import imgZip from '../../../../assets/images/icon-zip.svg';
import imgJpg from '../../../../assets/images/icon-jpg.svg';
import imgPng from '../../../../assets/images/icon-png.svg';
import { SessionExpiredException } from '../../../../utils/errors';

const UploadFiles = forwardRef((props, ref) => {
  const {
    initialFiles: initialFilesProperties, id, action, origin, requestType,
    typeUser, fileSize, serviceAction,
  } = props;

  const navigate = useNavigate();

  const [loadingDownload, setLoadingDownload] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [fileError, setFileError] = useState('');
  const [initialFiles, setInitialFiles] = useState([]);
  const [notTypeFiles, setNotTypeFiles] = useState([]);

  const getIconByName = (namee) => {
    let extension = namee.split('.');

    extension = extension[extension.length - 1];

    switch (extension) {
      case 'pdf':
        return { thumbUrl: imgPdf };
      case 'zip':
        return { thumbUrl: imgZip };
      case 'jpg':
        return { thumbUrl: imgJpg };
      case 'jpeg':
        return { thumbUrl: imgJpg };
      case 'png':
        return { thumbUrl: imgPng };
      default:
        return { thumbUrl: imgZip };
    }
  };

  const validateFiles = (): boolean => {
    const types = ['pdf', 'zip', 'rar', 'jpg', 'jpeg', 'png'];
    let totalSize = 0;
    let typeError = false;

    fileList.forEach((file) => {
      if (file.id) {
        totalSize += file.size;
      } else {
        let extension = file.name.split('.');
        extension = extension[extension.length - 1];
        typeError = typeError || !types.includes(extension);
        totalSize += file.originFileObj.size;
      }
    });

    // 1048576 = 1mb
    if (totalSize > fileSize) {
      setFileError('Os arquivos devem conter menos do que o definido nos parâmetros do sistema');
      return false;
    }

    if (typeError) {
      setFileError('Os arquivos devem estar no formato PDF, ZIP, RAR, JPG, JPEG ou PNG');
      return false;
    }

    setFileError('');
    return true;
  };

  const handleChange = (allChanges): void => {
    setFileList(allChanges.fileList.map((v) => ({ ...v, ...getIconByName(v.name) })));
    validateFiles();
  };

  const handleSubmitFiles = async (): Promise<boolean> => {
    let response;

    if (validateFiles()) {
      let url;
      let requestFunction;

      switch (true) {
        case (requestType === 'SERVICO' && (origin === 'details' || origin === 'summary')):
          url = '/file/customer/editServiceRequestFiles';
          requestFunction = updateFiles;
          break;

        case (requestType === 'SERVICO' && action === 'edit' && serviceAction !== 'Pós-vendas'):
          url = '/file/editServiceRequestFiles';
          requestFunction = updateFiles;
          break;

        case (requestType === 'SERVICO' && action === 'edit' && serviceAction === 'Pós-vendas'):
          url = '/file/reportAttachmentFiles';
          requestFunction = UploadFilesReports;
          break;

        case (requestType === 'ORCAMENTO' && (origin === 'details' || origin === 'summary')):
          url = '/file/customer/editBudgetRequestFiles';
          requestFunction = updateFiles;
          break;

        case (requestType === 'ORCAMENTO' && action === 'edit'):
          url = '/file/editBudgetRequestFiles';
          requestFunction = updateFiles;
          break;

        default:
          break;
      }

      const fList = [];

      if (typeUser != '') {
        for (const k in fileList) {
          fList.push(fileList[k]);
        }

        for (const k in notTypeFiles) {
          fList.push(notTypeFiles[k]);
        }
      }

      try {
        response = await requestFunction({
          url,
          fileList: typeUser !== '' ? fList : fileList,
          id,
        });
      } catch (error) {
        if (!(error instanceof SessionExpiredException)) {
          setFileError('Ocorreu um erro no upload dos arquivos, tente novamente');
        }

        message.error('Ocorreu um erro no upload dos arquivos, tente novamente');
        throw new Error('Ocorreu um erro no upload dos arquivos, tente novamente');
      }

      if (!response?.body.error) {
        return true;
      }

      setFileError('Ocorreu um erro no upload dos arquivos, tente novamente');
      message.error('Ocorreu um erro no upload dos arquivos, tente novamente');
      throw new Error('Ocorreu um erro no upload dos arquivos, tente novamente');
    }

    return false;
  };

  const handleDownload = async (file): Promise<void> => {
    setLoadingDownload(true);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    let url = '/file/downloadFile/';

    if (getUserType() === 'COOPERATIVE') {
      url = '/file/customer/downloadFile/';
    }

    if (file.type === 'shapefile') {
      url = '/file/downloadShapefile/';
    }

    if (file.type === 'kml') {
      url = '/file/downloadKml/';
    }

    if (file.type === 'kmz') {
      url = '/file/downloadKmz/';
    }

    try {
      const downloadFileReqRef: RequestWrapper = get({
        url: `${url}${file.id}`,

        frontConfigs: {
          navigateFunction: navigate,
        },

        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
        },

        responseType: 'blob',
      });

      response = await downloadFileReqRef;
    } catch (error) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    setLoadingDownload(false);

    switch (true) {
      case (response?.statusCode === 200):
        downloadFile(response?.body, file.name);
        break;

      case (responseError?.body?.code === -9002):
        // "File not found", -9002
        notification.error({
          message: 'Erro',
          description: 'Arquivo não encontrado.',
        });
        break;

      case (responseError?.body?.code === -9008):
        // "Shapefile not found", -9008
        notification.error({
          message: 'Erro',
          description: 'Shapefile não encontrado.',
        });
        break;

      case (responseError?.body?.code === -9009):
        // "Shapefile could not be generated", -9009
        notification.error({
          message: 'Erro',
          description: 'Falha ao gerar o shapefile.',
        });
        break;

      case (responseError?.body?.code === -9011):
        // "Kml not found", -9011
        notification.error({
          message: 'Erro',
          description: 'KML não encontrado.',
        });
        break;

      case (responseError?.body?.code === -9012):
        // "Kml could not be generated", -9012
        notification.error({
          message: 'Erro',
          description: 'Falha ao gerar o KML.',
        });
        break;

      case (responseError?.body?.code === -9013):
        // "Kmz not found", -9013
        notification.error({
          message: 'Erro',
          description: 'KMZ não encontrado.',
        });
        break;

      case (responseError?.body?.code === -9014):
        // "Kmz could not be generated", -9014
        notification.error({
          message: 'Erro',
          description: 'Falha ao gerar o KMZ.',
        });
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: 'Desculpe, você não possui permissão para executar esta ação.',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: 'Erro',
          description: 'Ocorreu um erro ao baixar o anexo, tente novamente.',
        });
        break;
    }
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div className="ant-upload-text">Adicionar anexos</div>
    </div>
  );

  // to cancel the default request
  const dummyRequest = ({ onSuccess }): void => {
    setTimeout(() => {
      onSuccess('ok');
    }, 0);
  };

  useEffect(() => {
    return () => {
      setLoadingDownload(false);
    };
  }, []);

  useEffect(() => {
    if (typeUser != '') {
      setNotTypeFiles(initialFiles.filter((i) => i.typeUser != typeUser && i.typeUser != null));
    }
    if (initialFiles.length !== initialFilesProperties.length) {
      setInitialFiles(initialFilesProperties);
      setFileList(initialFilesProperties.map((v) => ({ ...v, ...getIconByName(v.name) })));
    }
  }, [initialFiles.length, initialFilesProperties]);

  useImperativeHandle(ref, () => ({
    validateFiles: (): boolean => validateFiles(),

    handleSubmitFiles: async (): Promise<boolean> => handleSubmitFiles(),
  }));

  return (
    <div className="UploadFiles">
      <Spin
        tip="Realizando Download..."
        spinning={loadingDownload}
      >
        <Upload
          listType="picture-card"
          fileList={
            typeUser != ''
              ? fileList.filter(
                (f) => f.typeUser == typeUser
                  || f.typeUser == null,
              ) : (fileList)
          }
          onDownload={handleDownload}
          showUploadList={{
            showPreviewIcon: false,
            showRemoveIcon: action !== 'list',
            showDownloadIcon: action === 'list' || origin === 'details' || typeUser === 'REPORT_ATTACHMENT',
          }}
          onChange={handleChange}
          customRequest={dummyRequest}
          multiple
        >
          {(
            (action !== 'list' && origin === 'details')
            || (action !== 'list' && origin === 'summary')
            || (action !== 'list' && origin === 'edit')
          ) && uploadButton}
        </Upload>
        <div className="file-error">
          {fileError}
        </div>
        <div style={{ display: 'none' }}>
          <img alt="" src={imgPdf} />
          <img alt="" src={imgZip} />
        </div>
      </Spin>
    </div>
  );
});

UploadFiles.propTypes = {
  initialFiles: PropTypes.arrayOf(PropTypes.any).isRequired,
  id: PropTypes.number,
  action: PropTypes.string.isRequired,
  serviceAction: PropTypes.string,
  origin: PropTypes.string,
  typeUser: PropTypes.string,
  fileSize: PropTypes.number,
  requestType: PropTypes.oneOf(['ORCAMENTO', 'SERVICO']).isRequired,
};

UploadFiles.defaultProps = {
  id: 0,
  typeUser: '',
  origin: '',
  serviceAction: '',
  fileSize: 1048576 * 8,
};

export default UploadFiles;
